import { Box, Button, CircularProgress, Container } from "@mui/material";
import { DataGrid } from "devextreme-react";
import { FilterRow, Selection } from "devextreme-react/data-grid";
import { Column, Editing } from "devextreme-react/data-grid";
import React from "react";
import { useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import cubeApiClient from "../../BusinessObjects/cubeApiService";
import { RootState } from "../../BusinessObjects/Redux/app/store";
import { ResourceNames } from "../../Utils/ResourceNames";
import notify from "devextreme/ui/notify";
import { ResourceView } from "../../Models/SysConf/ResourceView";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import FieldTypes from "../../Utils/FieldTypes";

type Props = {};

const ResourcesViewsPage = (props: Props) => {
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const tokenApi = useSelector((state: RootState) => state.user.tokenAPI);
  const userProfile = useSelector((state: RootState) => state.user.userProfile);

  const queryClient = useQueryClient();

  let dataGridRef = React.createRef<DataGrid>();

  let [searchParams] = useSearchParams();

  const [isLoadingDeleteResourceView, setIsLoadingDeleteResourceView] =
    React.useState<boolean>(false);
  const [isLoadingSaveResourceView, setIsLoadingSaveResourceView] =
    React.useState<boolean>(false);

  const [resourceViewObject, setResourceViewObject] = React.useState<
    ResourceView | undefined
  >(undefined);

  const [resourceViewToDuplicate, setResourceViewToDuplicate] = React.useState<
    ResourceView | undefined
  >(undefined);

  // Hook di navigazione
  const navigate = useNavigate();

  const {
    data: resourcesViews,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery(
    ["resourcesViews", tokenApi],
    () => {
      const service = new cubeApiClient();

      return service.getItems(
        `api/sys/${ResourceNames.ResourcesViewes}`,
        tokenApi
      );
    },
    {
      enabled: isLoggedIn,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchIntervalInBackground: false,
    }
  );

  React.useEffect(() => {
    return () => {
      queryClient.removeQueries(["resourcesViews", tokenApi]);
      queryClient.removeQueries([
        "resourcesViewsLinks",
        resourceViewObject?.View_ID,
        tokenApi,
      ]);
      queryClient.removeQueries([
        "resourcesViewsFields",
        resourceViewObject?.View_ID,
        tokenApi,
      ]);
      queryClient.removeQueries([
        "resourcesViewsFilters",
        resourceViewObject?.View_ID,
        tokenApi,
      ]);
      queryClient.removeQueries([
        "resourcesViewsSortFields",
        resourceViewObject?.View_ID,
        tokenApi,
      ]);
      queryClient.removeQueries(["allResourcesFields", tokenApi]);
    };
  }, []);

  const deleteResourceView = (e: ResourceView[]) => {
    const service = new cubeApiClient();

    setIsLoadingDeleteResourceView(true);

    service
      .deleteObject(e, `api/sys/${ResourceNames.ResourcesViewes}`, tokenApi)
      .then(
        (data) => {
          setIsLoadingDeleteResourceView(false);
          notify("Job Eliminato con successo", "success", 2000);
          refetch();
        },
        (error) => {
          setIsLoadingDeleteResourceView(false);
          notify("Errore nella cancellazione del Job", "error", 2000);
          refetch();
        }
      );
  };

  const duplicateResourceView = () => {
    const service = new cubeApiClient();

    setIsLoadingSaveResourceView(true);

    service.duplicateResourceView(resourceViewToDuplicate!, tokenApi).then(
      (data) => {
        setIsLoadingSaveResourceView(false);
        notify("Job Duplicato con successo", "success", 2000);
        refetch();
      },
      (error) => {
        setIsLoadingSaveResourceView(false);
        notify("Errore nella duplicazione del Job", "error", 2000);
        refetch();
      }
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={duplicateResourceView}
          disabled={resourceViewToDuplicate === undefined ? true : false}
        >
          Duplica
        </Button>
        <Button
          onClick={() => {
            navigate("new");
          }}
          variant="contained"
        >
          Aggiungi
        </Button>
      </Box>
      <DataGrid
        ref={dataGridRef}
        dataSource={resourcesViews}
        showBorders={true}
        showColumnHeaders={true}
        showRowLines={true}
        showColumnLines={true}
        onRowRemoved={(e) => {
          deleteResourceView(e.data);
        }}
        onEditingStart={(e) => {
          navigate(e.data.View_ID.toString());
        }}
        onSelectionChanged={(e) =>
          setResourceViewToDuplicate(e.selectedRowsData[0])
        }
        disabled={isRefetching || isLoadingDeleteResourceView}
      >
        <FilterRow visible={true} />
        <Selection mode={"multiple"} />
        <Column
          dataField="ViewDescription"
          caption="Descrizione"
          dataType="string"
        />

        <Column dataField="ResourceName" caption="Risorsa" dataType="string" />

        <Column
          dataField="JumpLabel"
          caption="Filtro su bottone di navigazione"
          dataType="string"
        />

        <Column dataField="UserName" caption="Utente" dataType="string" />

        <Editing
          allowUpdating={true}
          allowDeleting={true}
          useIcons={true}
          confirmDelete={true}
        />
      </DataGrid>
    </Container>
  );
};

export default ResourcesViewsPage;
